import { useState } from 'react';
import type { ScriptStep } from '@packages/global';
import { ScriptMode, EpisodeStatus } from '@packages/global';
import { StepNavigator } from '../steps';
import { ScriptActionButtons, ScriptButtonState, ModeIndicatorCompact } from '../common';
import { DocumentTextIcon, XMarkIcon, ChevronDownIcon, MusicalNoteIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

/**
 * LeftNavigationPanel 组件属性
 * 用于脚本生成页面的左侧导航面板
 */
export interface LeftNavigationPanelProps {
  /** 原始用户需求文本 */
  requirement: string;
  /** 步骤列表 */
  steps: ScriptStep[];
  /** 当前激活的步骤编号 */
  activeStep: number;
  /** 步骤点击回调 */
  onStepClick: (stepNumber: number) => void;
  /** 重新生成当前步骤回调 */
  onRegenerateStep: () => void;
  /** 生成分镜回调 */
  onGenerateStoryboard: () => void;
  /** 查看视频回调 */
  onViewVideo?: () => void;
  /** 生成视频回调 */
  onGenerateVideo?: () => void;
  /** 重新生成分镜回调（已有分镜时可用） */
  onRegenerateStoryboard?: () => void;
  /** 重试回调 */
  onRetry?: () => void;
  /** 强制重试回调（用于卡住的任务） */
  onForceRetry?: () => void;
  /** 是否所有步骤都已完成 */
  allStepsCompleted: boolean;
  /** 是否正在生成中 */
  isGenerating: boolean;
  /** 是否正在处理中 */
  isProcessing?: boolean;
  /** 是否生成失败 */
  isFailed?: boolean;
  /** 错误信息 */
  errorMessage?: string;
  /** 是否已激活（当前脚本） */
  isActiveScript?: boolean;
  /** 是否有其他已激活脚本 */
  hasOtherActiveScript?: boolean;
  /** 脚本模式（灵活/严格） */
  scriptMode?: ScriptMode;
  /** Episode 状态 - 用于判断是否已有分镜 */
  episodeStatus?: EpisodeStatus;
  /** 原始剧本内容（FILE_SCRIPT 或 PROJECT_SCRIPT 模式） */
  uploadedScriptContent?: string;
  /** 上传文件原始 URL（MV 模式：音频文件） */
  uploadedFileUrl?: string;
}

/**
 * LeftNavigationPanel 组件
 * 
 * 实现左侧导航面板，包含三个区域：
 * 1. RequirementSection: 显示用户原始需求
 * 2. StepsSection: 步骤导航器，显示所有 6 个步骤
 * 3. ActionSection: 操作按钮（重新生成、生成分镜）
 * 
 * 设计参考：Pencil 设计中的 LeftNavPanel 节点
 * - width: 280px
 * - 右侧边框: #1F1F23
 * - fill: $bg-page
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6
 */
export default function LeftNavigationPanel({
  requirement,
  steps,
  activeStep,
  onStepClick,
  onRegenerateStep,
  onGenerateStoryboard,
  onViewVideo,
  onGenerateVideo,
  onRegenerateStoryboard,
  onRetry,
  onForceRetry,
  allStepsCompleted,
  isGenerating,
  isProcessing = false,
  isFailed = false,
  errorMessage,
  isActiveScript = false,
  hasOtherActiveScript = false,
  scriptMode = ScriptMode.PROMPT,
  episodeStatus,
  uploadedScriptContent,
  uploadedFileUrl,
}: LeftNavigationPanelProps) {
  // 查看原始剧本弹窗状态
  const [showScriptModal, setShowScriptModal] = useState(false);
  // MV 音频分析结果折叠状态（默认隐藏）
  const [showMvAnalysis, setShowMvAnalysis] = useState(false);

  // 是否显示查看原始剧本按钮（FILE_SCRIPT 或 PROJECT_SCRIPT 模式且有内容）
  const canViewOriginalScript = (scriptMode === ScriptMode.FILE_SCRIPT || scriptMode === ScriptMode.PROJECT_SCRIPT) && uploadedScriptContent;

  // MV 模式：解析音频分析结果
  const mvAnalysis = scriptMode === ScriptMode.MV && uploadedScriptContent
    ? (() => {
      try { return JSON.parse(uploadedScriptContent); } catch { return null; }
    })()
    : null;

  // 计算已完成步骤数
  const completedStepsCount = steps.filter(
    (step) => step.status === 'completed'
  ).length;

  // 判断是否已有分镜（Episode 状态不是 DRAFT）
  const hasStoryboard = episodeStatus && episodeStatus !== EpisodeStatus.DRAFT;

  // 计算按钮状态
  const getButtonState = (): ScriptButtonState => {
    if (isProcessing) return ScriptButtonState.PROCESSING;
    if (isGenerating) return ScriptButtonState.GENERATING;
    if (isFailed) return ScriptButtonState.FAILED;
    // 如果已有分镜，显示查看视频
    if (hasStoryboard) return ScriptButtonState.VIEW_VIDEO;
    if (isActiveScript) return ScriptButtonState.VIEW_VIDEO;
    if (hasOtherActiveScript && allStepsCompleted) return ScriptButtonState.GENERATE_VIDEO;
    return ScriptButtonState.GENERATE_STORYBOARD;
  };

  return (
    <>
      <aside
        className="w-[280px] flex-shrink-0 flex flex-col h-full border-r border-border-subtle bg-bg-surface"
      >
        {/* RequirementSection: 用户需求展示 */}
        <section className="p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
              用户需求
            </h3>
            <ModeIndicatorCompact mode={scriptMode} />
          </div>
          <p className="text-sm text-gray-300 leading-relaxed line-clamp-4">
            {requirement || '暂无需求描述'}
          </p>

          {/* MV 模式：音频文件预览 */}
          {scriptMode === ScriptMode.MV && uploadedFileUrl && (
            <div className="flex flex-col gap-2">
              <span className="text-xs text-gray-400">原始音频</span>
              <audio
                src={uploadedFileUrl}
                controls
                className="w-full h-8 dark:[filter:invert(1)_hue-rotate(180deg)]"
              />
            </div>
          )}

          {/* MV 模式：音频分析结果摘要（可折叠，默认隐藏） */}
          {scriptMode === ScriptMode.MV && mvAnalysis && (
            <div className="flex flex-col gap-0 rounded-lg bg-bg-elevated border border-border-subtle overflow-hidden">
              <button
                onClick={() => setShowMvAnalysis(v => !v)}
                className="flex items-center justify-between px-3 py-2 text-xs text-gray-400 hover:text-gray-300 transition-colors cursor-pointer w-full"
              >
                <div className="flex items-center gap-1.5">
                  <MusicalNoteIcon className="w-3.5 h-3.5 text-primary" />
                  <span>音频分析结果</span>
                </div>
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${showMvAnalysis ? 'rotate-180' : ''}`} />
              </button>
              {showMvAnalysis && (
                <div className="flex flex-col gap-1.5 px-3 pb-3 border-t border-border-subtle">
                  <div className="h-1" />
                  {mvAnalysis.title && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">标题</span>
                      <span className="text-xs text-gray-300 truncate max-w-[140px]">{mvAnalysis.title}</span>
                    </div>
                  )}
                  {mvAnalysis.totalDuration != null && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">时长</span>
                      <span className="text-xs text-gray-300">
                        {Math.floor(mvAnalysis.totalDuration / 60)}:{String(Math.round(mvAnalysis.totalDuration % 60)).padStart(2, '0')}
                      </span>
                    </div>
                  )}
                  {mvAnalysis.bpm != null && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">BPM</span>
                      <span className="text-xs text-gray-300">{Math.round(mvAnalysis.bpm)}</span>
                    </div>
                  )}
                  {mvAnalysis.genre && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">风格</span>
                      <span className="text-xs text-gray-300 truncate max-w-[140px]">{mvAnalysis.genre}</span>
                    </div>
                  )}
                  {mvAnalysis.mood && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">情绪</span>
                      <span className="text-xs text-gray-300 truncate max-w-[140px]">{mvAnalysis.mood}</span>
                    </div>
                  )}
                  {mvAnalysis.segments?.length > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">分段数</span>
                      <span className="text-xs text-gray-300">{mvAnalysis.segments.length} 段</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* MV 模式：分析中提示 */}
          {scriptMode === ScriptMode.MV && !mvAnalysis && uploadedFileUrl && (
            <div className="text-xs text-gray-400 italic">音频分析中...</div>
          )}

          {/* FILE_SCRIPT 模式：原始文件下载链接 */}
          {scriptMode === ScriptMode.FILE_SCRIPT && uploadedFileUrl && (
            <a
              href={uploadedFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">查看原始附件</span>
            </a>
          )}

          {/* 查看原始剧本按钮 */}
          {canViewOriginalScript && (
            <button
              onClick={() => setShowScriptModal(true)}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary transition-colors cursor-pointer"
            >
              <DocumentTextIcon className="w-3.5 h-3.5" />
              <span>查看原始剧本</span>
            </button>
          )}
        </section>

        {/* StepsSection: 步骤导航器 */}
        <section className="flex-1 flex flex-col overflow-y-auto">
          <div className="px-4 py-3 flex items-center justify-between">
            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
              生成步骤
            </h3>
            <span className="text-xs text-gray-400">
              {completedStepsCount}/{steps.length} 完成
            </span>
          </div>

          {/* 使用 StepNavigator 组件 */}
          <StepNavigator
            steps={steps}
            activeStep={activeStep}
            onStepClick={onStepClick}
            scriptMode={scriptMode}
          />
        </section>

        {/* ActionSection: 操作按钮 */}
        <section className="p-4 flex flex-col gap-3 border-t border-border-subtle">
          {/* 进度行 - 设计图样式 */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-400">完成进度</span>
            <div className={`flex items-center gap-1 px-2 py-1 rounded ${allStepsCompleted
              ? 'bg-success/15'
              : 'bg-primary/15'
              }`}>
              <span className={`text-xs font-semibold ${allStepsCompleted ? 'text-success' : 'text-primary'}`}>
                {completedStepsCount}/{steps.length}
              </span>
              <span className={`text-[11px] ${allStepsCompleted ? 'text-success/70' : 'text-primary/70'}`}>
                {allStepsCompleted ? '已完成' : '进行中'}
              </span>
            </div>
          </div>

          {/* 使用 ScriptActionButtons 组件 */}
          <ScriptActionButtons
            buttonState={getButtonState()}
            completedSteps={completedStepsCount}
            totalSteps={steps.length}
            scriptMode={scriptMode}
            errorMessage={errorMessage}
            onGenerateStoryboard={onGenerateStoryboard}
            onViewVideo={onViewVideo}
            onGenerateVideo={onGenerateVideo}
            onRegenerateStoryboard={onRegenerateStoryboard}
            onRegenerate={onRegenerateStep}
            onRetry={onRetry}
            onForceRetry={onForceRetry}
            showProgress={false}
          />


        </section>
      </aside>

      {/* 原始剧本查看弹窗 */}
      {
        showScriptModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
            <div className="bg-bg-elevated rounded-xl border border-border-subtle w-[700px] max-h-[80vh] flex flex-col shadow-2xl">
              {/* 弹窗头部 */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
                <div className="flex items-center gap-2">
                  <DocumentTextIcon className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold text-gray-50">原始剧本内容</h3>
                </div>
                <button
                  onClick={() => setShowScriptModal(false)}
                  className="p-1.5 rounded-lg hover:bg-bg-surface transition-colors cursor-pointer"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              {/* 弹窗内容 */}
              <div className="flex-1 overflow-y-auto p-6">
                <pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">
                  {uploadedScriptContent}
                </pre>
              </div>
              {/* 弹窗底部 */}
              <div className="px-6 py-4 border-t border-border-subtle flex justify-end">
                <button
                  onClick={() => setShowScriptModal(false)}
                  className="px-4 py-2 text-sm text-gray-300 hover:text-gray-50 bg-bg-surface hover:bg-bg-elevated rounded-lg transition-colors cursor-pointer"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
}
